import React, { useEffect, useState } from "react";
import "../styles/myplan.css";
import moment from "moment";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  Dropdown,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Loader from "./Common/Loader";
import { DataSeriesTypes } from "../types";
import { useDispatch, useSelector } from "react-redux";
import { getKWHPrice } from "../store/actions";
import { NoResult } from "./Common/NoResult";
import { Context } from "../pages/LoginMarket";
import {
  exportUSNPlan,
  fetchUSNMarketPlanDetail,
  fetchUSNMarketPlan,
  fetchUSNStateProviders,
} from "../api/usnMarket";
import Datepicker from "./Datepicker";
import { selectUtility } from "../store/utility/selector";
import { RankChart } from "./Charts/RankChart";
import { Tooltip, Typography } from "@mui/material";
import { useLocation, useHistory } from "react-router-dom";
import { selectMyProvider } from "../store/myProvider/selector";
import { MultiSelectFilterDropdown } from "./Common/MultiSelectFilterDropdown";
import { usePersistedPagination } from "./Hooks/usePersistedPagination";
import { useTableSortReset } from "./Hooks/useTableSortReset";
import {
  extractUniqueSources,
  extractUniqueTerms,
  matchesSourceFilter,
  matchesTermFilter,
} from "../utils/planFilters";
import {
  createNumericSortFunction,
  createStringSortFunction,
} from "../utils/planTableSort";
import {
  ActiveFilterTags,
  activeFilterTagClassName,
} from "./Common/ActiveFilterTags";

interface topPlanObj {
  base_rate: String;
  cancel_fee: String;
  created_ts: String;
  diff: any;
  efl_url: string;
  energy_charge1: any;
  plan_id: String;
  plan_name: String;
  price: String;
  provider_id: String;
  provider_name: String;
  term: string;
  utility_id: String;
  utility_name: String;
  rank: any;
  source: any;
  contract_term: String;
}
interface detail {
  plan: any;
  rank: any;
}

const calDate = (val: string) => {
  return moment(val, "YYYY-MM-DD").subtract(30, "days").format("YYYY-MM-DD");
};

const USN_AllPlansMarketView = () => {
  const currentLocation = useSelector<
    any,
    { state_id: string; state_name: string }
  >((state) => state.Location.currentLocation);
  const myProvider = useSelector(selectMyProvider);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const [filterValue, setFilterValue] = useState({
    created_ts: moment().format("YYYY-MM-DD"),
    provider_id: "",
  });
  const [dataPlanList, setDataPlanList] = useState<Array<topPlanObj>>([]);
  const [checked, setChecked] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [showMeData, setShowMeData] = useState<Array<topPlanObj>>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [openSource, setOpenSource] = useState(false);
  const {
    tablePage,
    setTablePage,
    rowsPerPage,
    handleRowsPerPageChange,
    resetPage,
    paginationRowsPerPageOptions,
  } = usePersistedPagination("usnAllPlans_rowsPerPage", 10);
  const { sortResetKey, resetTableSort } = useTableSortReset();
  const [dropdownOpenMap, setDropdownOpenMap] = useState<Record<string, boolean>>({});
  const currentUtility = useSelector(selectUtility);
  const [providerList, setProviderList] = useState<Array<{ provider_id: string; provider_name: string }>>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [openProvider, setOpenProvider] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const getPlanList = async (val: any) => {
    setIsLoading(true);
    setChecked(false);
    setDataPlanList([]);
    resetPage();
    const res = await fetchUSNMarketPlan(
      currentLocation?.state_id,
      val.created_ts,
      currentUtility.utility_id,
      "1",
      val.provider_id
    );

    if (res.status !== 1 || res.response.length === 0) {
      return setIsLoading(false);
    }

    let iRank = 1;
    setDataPlanList(
      res.response.map((item: any) => {
        return {
          ...item,
          rank: iRank++,
        };
      })
    );
    setIsLoading(false);
  };
  useEffect(() => {
    if (currentUtility) {
      getPlanList(filterValue);
    }
  }, [filterValue, currentUtility]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const created_ts = searchParams.get("created_ts");
    const term = searchParams.get("term");
    const provider_id = searchParams.get("provider_id");

    setFilterValue((prev) => ({
      ...prev,
      created_ts: created_ts || prev.created_ts,
      provider_id: provider_id || "",
    }));

    if (term) {
      setSelectedTerms([String(term).trim()]);
    }
    if (provider_id) {
      setSelectedProviderId(provider_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeProviderList = (res: any): Array<{ provider_id: string; provider_name: string }> => {
    if (!res) return [];
    const raw =
      res.response ??
      res.data ??
      res.usn_provider_list ??
      (Array.isArray(res) ? res : []);
    const arr = Array.isArray(raw) ? raw : (raw?.usn_provider_list ?? raw?.list ?? []);
    if (!Array.isArray(arr)) return [];
    return arr.map((item: any) => ({
      provider_id: String(item.provider_id ?? item.id ?? ""),
      provider_name: String(item.provider_name ?? item.name ?? ""),
    })).filter((p: { provider_id: string; provider_name: string }) => p.provider_id || p.provider_name);
  };

  useEffect(() => {
    const loadProviders = async () => {
      if (!currentLocation || !currentUtility) return;
      try {
        const res = await fetchUSNStateProviders(
          currentLocation.state_id,
          "1",
          currentUtility.utility_id
        );
        const list = normalizeProviderList(res);
        setProviderList(list);
      } catch {
        setProviderList([]);
      }
    };

    loadProviders();
  }, [currentLocation, currentUtility]);

  const changeDate = (val: any) => {
    setFilterValue({
      ...filterValue,
      created_ts: moment(val).format("YYYY-MM-DD"),
    });
    resetTableSort();
  };

  const changeProvider = (provider_id: string) => {
    setSelectedProviderId(provider_id);
    setFilterValue((prev) => ({
      ...prev,
      provider_id,
    }));
    resetTableSort();
  };

  const showMeOnly = (flag: boolean) => {
    setChecked(flag);
    resetPage();
    resetTableSort();
    let myData: any = [];

    dataPlanList.forEach((item) => {
      if (item?.provider_id === myProvider?.provider_id) {
        myData.push(item);
      }
    });
    setShowMeData(myData);
  };

  const handleTermsChange = (terms: string[]) => {
    setSelectedTerms(terms);
    resetTableSort();
  };

  const handleSourcesChange = (sources: string[]) => {
    setSelectedSources(sources);
    resetTableSort();
  };

  const [open, setOpen] = React.useState(false);
  const [isShow, setIsShow] = useState(false);
  const [planDetail, setPlanDetail] = useState<detail>({
    plan: {},
    rank: 0,
  });

  const [detailData, setDetailData] = useState<DataSeriesTypes[]>([]);
  const [openSelect, setOpenSelect] = useState(false);
  const [hasRankTrend, setHasRankTrend] = useState(true);

  const handleOpen = async (plan_id: any, pd_id: any, rank: any) => {
    setOpen(true);
    setIsShow(true);
    dispatch(getKWHPrice(true));
    const res = await fetchUSNMarketPlanDetail(
      plan_id,
      pd_id,
      `${calDate(filterValue.created_ts)}&${filterValue.created_ts}`
    );
    let detailDataArr: any[] = [];
    setHasRankTrend(true);
    if (res.status !== 1 || res.response.rank_trend.length === 0) {
      detailDataArr = [];
      setHasRankTrend(false);
      return setIsShow(false);
    }

    res.response.rank_trend.map((item: any) => {
      if (item.rank !== 0)
        return detailDataArr.push({ x: item.date, y: item.rank });
      else return detailDataArr.push({ x: item.date, y: null });
    });

    setDetailData([
      { id: "Rank", data: detailDataArr.filter((i: any) => i.y) },
    ]);
    setPlanDetail({
      plan: res.response.plan,
      rank: rank,
    });
    setIsShow(false);
  };

  const handleClose = () => {
    setOpen(false);
    dispatch(getKWHPrice(false));
  };

  const handleJumpToPlanInsight = () => {
    if (!planDetail.plan) return;
    const params = new URLSearchParams();
    if (filterValue.created_ts) {
      params.set("created_ts", filterValue.created_ts);
    }
    if (planDetail.plan.contract_term) {
      params.set("term", String(planDetail.plan.contract_term));
    }
    if (planDetail.plan.provider_id) {
      params.set("provider_id", String(planDetail.plan.provider_id));
    }
    history.push(`/plan-insight?${params.toString()}`);
    setOpen(false);
    dispatch(getKWHPrice(false));
  };

  const exportList = async () => {
    setIsDownload(true);
    const res = await exportUSNPlan(
      currentLocation?.state_id,
      filterValue.created_ts,
      "1"
    );

    const fileName = res.headers
      .get("content-disposition")
      ?.split("filename=")[1];

    const data = await res.blob();

    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = url;
    link.setAttribute(
      "download",
      fileName || `PlanList_${filterValue.created_ts}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownload(false);
  };

  const conditionalRowStyles: any = [
    {
      when: (row: any) => row?.provider_id === myProvider?.provider_id,
      style: { backgroundImage: "linear-gradient(-90deg,#ffffff, #F3EEFE)" },
    },
  ];

    const toggleDropdown = (planId: string) => {
    setDropdownOpenMap(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#F2F2F2",
        paddingRight: 0,
        '&:last-child': {
          minWidth: '300px'
        }
      },
    },
    cells: {
      style: {
        '&:last-child': {
          minWidth:"300px",
          flexWarp:'wrap',

          div: {
            overflow: 'visible !important',
          },
          button: {
            marginRight: "0.5rem"
          }
        },
      },
    },
  };

  const formatCreditValue = (credit: unknown) => {
    const value = Number(credit || 0);
    return `$${value.toFixed(2)}`;
  };

  const buildCreditTooltip = (planInfo: any) => {
    const tiers = [1, 2, 3]
      .map((tier) => {
        const amount = Number(planInfo?.[`credit${tier}`] || 0);
        if (!amount) {
          return "";
        }

        const start = Number(planInfo?.[`credit${tier}_kwh_start`] || 0);
        const end = Number(planInfo?.[`credit${tier}_kwh_end`] || 0);
        const endText = end ? ` and <= ${end} kWh` : "";
        return `Tier ${tier}: $${amount.toFixed(0)} rebate when usage > ${start} kWh${endText}.`;
      })
      .filter(Boolean);

    return tiers.join(" ");
  };

  const realData = checked ? showMeData : dataPlanList;
  let filterData = realData.filter((item) =>
    matchesTermFilter(item, selectedTerms, "contract_term")
  );
  filterData = filterData.filter((item) =>
    matchesSourceFilter(item, selectedSources)
  );

  const filterTerms = extractUniqueTerms(realData, "contract_term");
  const filterSources = extractUniqueSources(realData);

  const renderTopPlanTable = () => {
    const columns: any[] = [
      {
        name: <span className="font-weight-bold fs-14">Rank</span>,
        selector: (row: any) => row.rank,
        width: "90px",
        sortable: true,
        sortFunction: createNumericSortFunction("rank"),
      },
      {
        name: <span className="font-weight-bold fs-14">Provider</span>,
        selector: (row: any) => row.provider_name,
        sortable: true,
        sortFunction: createStringSortFunction("provider_name"),
      },
      {
        name: <span className="font-weight-bold fs-14">Plan Name</span>,
        selector: (row: any) => {
          return (
            <Tooltip 
              title={row.plan_name} 
              placement="top"
              id={row.plan_id}
              arrow
            >
              <div className="plan-name-cell">
                <span className="text-truncate d-inline-block font-weight-bold fs-14">
                  {row.plan_name}
                </span>
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        sortFunction: createStringSortFunction("plan_name"),
      },
      {
        name: <span className="font-weight-bold fs-14">Rate</span>,
        selector: (row: any) => row.rate,
        cell: (row: any) => `${row.rate}¢`,
        sortable: true,
        sortFunction: createNumericSortFunction("rate"),
      },
      {
        name: <span className="font-weight-bold fs-14">Term</span>,
        selector: (row: any) => row.contract_term,
        sortable: true,
        sortFunction: createNumericSortFunction("contract_term"),
        style: {
          paddingLeft: "1.5rem",
        },
        width: "95px",
      },
      {
        name: (
          <span className="font-weight-bold fs-14 text-nowrap">
            Cancellation Fee
          </span>
        ),
        selector: (row: any) => row.cancel_fee,
        sortable: true,
        sortFunction: createNumericSortFunction("cancel_fee"),
        width: "148px",
        minWidth: "148px",
      },
      {
        name: <span className="font-weight-bold fs-14">Credit</span>,
        selector: (row: any) => row.credit1,
        cell: (row: any) => (
          <div className="d-flex align-items-center">
            <span>{formatCreditValue(row.credit1)}</span>
            {Number(row.credit1 || 0) > 0 && (
              <Tooltip title={buildCreditTooltip(row)}>
                <img src="/img/icon_tips.png" height={14} className="ms-1" alt="" />
              </Tooltip>
            )}
          </div>
        ),
        width: "110px",
        grow: 0,
        sortable: true,
        sortFunction: createNumericSortFunction("credit1"),
      },
      {
        name: <span className="font-weight-bold fs-14">Incentives</span>,
        selector: (row: any) => row.gift || '',
        cell: (row: any) => {
          if(!row.gift) return '';
          return (
            <Tooltip 
              title={row.gift} 
              placement="top"
              id={row.plan_id}
              arrow
            >
              <i className="bx bx-gift"></i>
            </Tooltip>
          );
        },
        width: "60px",
        sortable: true,
        sortFunction: createStringSortFunction("gift"),
      },
      {
        name: <span className="font-weight-bold fs-14">Source</span>,
        selector: (row: any) => row.source,
        sortable: true,
        sortFunction: createStringSortFunction("source"),
      },
      {
        name: <span className="font-weight-bold fs-14"></span>,
        selector: (row: any) => {
          const documents = row.documents || [];
          const sortedDocuments = [...documents].sort((a, b) => 
            a.description.length - b.description.length
          );
          
          const visibleButtons = [];
          const hiddenButtons = [];
          visibleButtons.push(
            <Button
              key="snapshot"
              color="secondary"
              outline
              className="btn btn-sm me-1"
              onClick={() => handleOpen(row.plan_id, row.pd_id, row.rank)}
            >
              Snapshot
            </Button>
          );
          
          if (sortedDocuments.length > 0) {
            const maxVisible = Math.min(1, sortedDocuments.length);
            for (let i = 0; i < maxVisible; i++) {
              visibleButtons.push(
                <Button
                  key={`doc-${i}`}
                  color="secondary"
                  outline
                  className="btn btn-sm me-1"
                  onClick={() => window.open(sortedDocuments[i].location)}
                >
                  {sortedDocuments[i].description}
                </Button>
              );
            }
            
          for (let i = maxVisible; i < sortedDocuments.length; i++) {
            hiddenButtons.push(
              <DropdownItem 
                key={`hidden-doc-${i}`}
                onClick={() => window.open(sortedDocuments[i].location)}
              >
                {sortedDocuments[i].description}
              </DropdownItem>
            );
            }
          }
          
          return (
            <div className="d-flex align-items-center flex-nowrap">
              {visibleButtons}
               {hiddenButtons.length > 0 && (
              <Dropdown
                isOpen={dropdownOpenMap[row.plan_id] || false}
                toggle={() => toggleDropdown(row.plan_id)}
              >
                <DropdownToggle tag="button" className="btn btn-sm btn-secondary outline me-1">
                  +{hiddenButtons.length}
                </DropdownToggle>
                <DropdownMenu
                  style={{ zIndex: "1" }}
                >
                  {hiddenButtons}
                </DropdownMenu>
              </Dropdown>
            )}
            </div>
          );
        },
        width: "240px",
      },
    ];
    return (
      <DataTable
        key={sortResetKey}
        columns={columns}
        keyField="plan_id"
        data={filterData}
        conditionalRowStyles={conditionalRowStyles}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={paginationRowsPerPageOptions}
        onChangeRowsPerPage={handleRowsPerPageChange}
        customStyles={customStyles}
        pagination
        paginationDefaultPage={tablePage}
        onChangePage={(page) => setTablePage(page)}
      />
    );
  };

  const filterSelections = () => {
    let list: React.ReactNode[] = [],
      key = 0;
    selectedTerms.forEach((term) => {
      list.push(
        <button
          key={key++}
          className={activeFilterTagClassName}
          onClick={() => {
            setSelectedTerms((prev) => prev.filter((item) => item !== term));
            resetTableSort();
          }}
        >
          {`Term: ${term} Month`}
          <i className="bx bx-x fs-15 ms-2" />
        </button>
      );
    });
    selectedSources.forEach((source) => {
      list.push(
        <button
          key={key++}
          className={activeFilterTagClassName}
          onClick={() => {
            setSelectedSources((prev) => prev.filter((item) => item !== source));
            resetTableSort();
          }}
        >
          {`Source: ${source}`}
          <i className="bx bx-x fs-15 ms-2" />
        </button>
      );
    });
    return <ActiveFilterTags tags={list} />;
  };

  return (
    <>
      <Card
        style={{
          position: "relative",
          paddingBottom: "1rem",
          minHeight: "40rem",
        }}
      >
        <CardHeader className="align-items-center justify-content-between d-flex">
          <div>
            <h4 className="card-title mb-0 flex-grow-1">{`All Market Plans (${filterData?.length || 0})`}</h4>
            {currentLocation && currentUtility && (
              <Typography
                variant="subtitle2"
                className="text-muted"
              >{`${currentLocation.state_name} - ${currentUtility.utility_name}`}</Typography>
            )}
          </div>
          <div className="flex-shrink-0">
            {!isDownload ? (
              <Button
                color="secondary"
                onClick={exportList}
                className="btn-label"
              >
                <i className="bx bx-download label-icon align-middle fs-17 me-2"></i>
                <span>Download</span>
              </Button>
            ) : (
              <Button color="secondary" className="btn-load" outline>
                <span className="d-flex align-items-center">
                  <Spinner size="sm" className="flex-shrink-0">
                    {" "}
                    Loading...{" "}
                  </Spinner>
                  <span className="flex-grow-1 ms-2">Loading...</span>
                </span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-4">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <MultiSelectFilterDropdown
                isOpen={openSelect}
                toggle={() => setOpenSelect(!openSelect)}
                options={filterTerms}
                selected={selectedTerms}
                onChange={handleTermsChange}
                allLabel="All Terms"
                formatOption={(value) => `${value} Month`}
                width={120}
              />
              <MultiSelectFilterDropdown
                isOpen={openSource}
                toggle={() => setOpenSource(!openSource)}
                options={filterSources}
                selected={selectedSources}
                onChange={handleSourcesChange}
                allLabel="All Sources"
                width={170}
                className="text-uppercase"
              />
            </div>
            <div className="d-flex align-item-center gap-3">
              <Dropdown isOpen={openProvider} toggle={() => setOpenProvider(!openProvider)}>
                <DropdownToggle tag="button" className="btn btn-light">
                  {selectedProviderId
                    ? providerList.find((p) => String(p.provider_id) === String(selectedProviderId))
                        ?.provider_name || "All Provider"
                    : "All Provider"}{" "}
                  <i className="mdi mdi-chevron-down"></i>
                </DropdownToggle>
                <DropdownMenu style={{ maxHeight: "20rem", overflowY: "auto" }}>
                  <DropdownItem onClick={() => changeProvider("")}>
                    All Provider
                  </DropdownItem>
                  {providerList.map((item) => (
                    <DropdownItem
                      key={item.provider_id}
                      onClick={() => changeProvider(String(item.provider_id))}
                    >
                      {item.provider_name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              <div className="form-check form-switch form-switch-md d-flex align-items-center form-switch-secondary">
                <Label className="text-muted d-flex align-items-center justify-content-between mb-0">
                  <Input
                    className="code-switch"
                    type="checkbox"
                    value=""
                    checked={checked}
                    onChange={(e) => {
                      showMeOnly(e.target.checked);
                    }}
                  />
                  View My Plans
                </Label>
              </div>
              <Datepicker
                handleChangeDate={(val: any) => changeDate(val)}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="mb-4 w-100">{filterSelections()}</div>
          {isLoading ? (
            <Loader />
          ) : (
            <div className="rdt_out">
              {dataPlanList?.length > 0 ? renderTopPlanTable() : <NoResult />}
            </div>
          )}
        </CardBody>
      </Card>
      <Modal size="lg" isOpen={open} toggle={handleClose} centered>
        <ModalHeader toggle={handleClose}>Plan Detail</ModalHeader>
        <div className="modal-body">
          {isShow ? (
            <Loader />
          ) : (
            <React.Fragment>
              <Table className="align-middle table-fixed mb-0">
                <thead>
                  <tr>
                    <th scope="col">Rank</th>
                    <th scope="col">Provider Name</th>
                    <th scope="col">Utility Name</th>
                    <th scope="col">Term</th>
                    <th scope="col">Base Rate</th>
                    <th scope="col">Plan Source</th>
                    <th scope="col">Cancellation Fee</th>
                    <th scope="col">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">{planDetail.rank}</th>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={handleJumpToPlanInsight}
                      >
                        {planDetail.plan.provider_name}
                      </button>
                    </td>
                    <td>{planDetail.plan.utility_name}</td>
                    <td>{planDetail.plan.contract_term}</td>
                    <td>
                      {planDetail.plan.rate}
                      {planDetail.plan.rate ? "¢" : ""}
                    </td>
                    <td>{planDetail.plan.source || ""}</td>
                    <td>{planDetail.plan.cancel_fee}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span>{formatCreditValue(planDetail.plan.credit1)}</span>
                        {Number(planDetail.plan.credit1 || 0) > 0 && (
                          <Tooltip title={buildCreditTooltip(planDetail.plan)}>
                            <img
                              src="/img/icon_tips.png"
                              height={14}
                              className="ms-1"
                              alt=""
                            />
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
              {hasRankTrend ? (
                <>
                  <RankChart title="Rank Trend" data={detailData} />
                </>
              ) : (
                <NoResult />
              )}
            </React.Fragment>
          )}
        </div>
      </Modal>
    </>
  );
};

export default USN_AllPlansMarketView;
